/**
 * Central environment resolution for every API client.
 *
 * Replaces the source's hardcoded environment switch, which was duplicated
 * across three config modules and left stuck on the development value — so
 * production builds pointed at localhost.
 *
 * All values come from REACT_APP_* vars, inlined by CRA at build time.
 * See .env.example for the full list.
 */

const stripTrailingSlash = (url) => (url || '').replace(/\/+$/, '');

export const API_BASE_URL = stripTrailingSlash(
  process.env.REACT_APP_API_URL || 'http://localhost:5001'
);

/** Backend API prefix for this client site. */
export const API_PREFIX = '/api/ivipass';

/** Build a full URL for a path relative to the ivipass API prefix. */
export const apiUrl = (path = '') =>
  `${API_BASE_URL}${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;

export const JSON_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * Shared response handler. Throws an Error carrying the server's message
 * where one is available, so callers can surface it directly.
 */
export const handleResponse = async (response) => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      (payload && (payload.message || payload.error)) ||
      `Request failed with status ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
};

/** Build a query string from an object, dropping empty values. */
export const buildQuery = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      search.append(key, String(value));
    }
  });
  return search.toString();
};

/** Client-side CSV download helper, shared by the internal tooling pages. */
export const exportToCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;

  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const str = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const csv = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
